import { useCallback, useMemo, useRef } from "react";
import { LoginRequiredError, useAuth as useAuthKit } from "@workos-inc/authkit-react";
import {
  clearWorkOSPendingAuthorization,
  markWorkOSAuthorizationStarted,
} from "@/lib/workos-callback";

type AuthKitAccessTokenGetter = ReturnType<typeof useAuthKit>["getAccessToken"];
type AuthKitAccessTokenOptions = Parameters<AuthKitAccessTokenGetter>[0];

/**
 * Wrapper around AuthKit's useAuth that supports test mode.
 *
 * When the URL contains `?test_mode=1` (or `localStorage` has `l9-test-mode:1`),
 * this hook returns a fake authenticated user and a no-op getAccessToken.
 * This lets Playwright E2E tests run without triggering the WorkOS
 * Hosted Sign-In redirect in the browser.
 *
 * The backend is expected to run with `TEST_MODE=true` so the fake
 * token is accepted (or the API bypasses JWT verification entirely).
 */

const TEST_MODE_KEY = "l9-test-mode";
const TEST_USER = {
  id: "test-user",
  email: "test@layer-nine.ai",
  firstName: "Test",
  lastName: "User",
  object: "user",
  emailVerified: true,
  profilePictureUrl: null,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
} as const;

function isTestMode(): boolean {
  try {
    // Check URL param first (for immediate boot)
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      if (params.get("test_mode") === "1") {
        // Persist for the session
        localStorage.setItem(TEST_MODE_KEY, "1");
        return true;
      }
      // Check localStorage (for subsequent navigations without the param)
      if (localStorage.getItem(TEST_MODE_KEY) === "1") {
        return true;
      }
    }
  } catch {
    // localStorage may not be available (private mode, etc.)
  }
  return false;
}

/**
 * In test mode the backend (chat-bridge) runs with `TEST_MODE=true` which
 * bypasses WorkOS/JWT verification entirely.  Returning an empty string here
 * prevents the UI from sending a fake "Bearer …" token that would be
 * rejected by the upstream agno-os backend (which still checks JWTs).
 */
async function fakeGetAccessToken(_options?: AuthKitAccessTokenOptions): Promise<string> {
  return "";
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const MAX_AUTH_WAIT_MS = 10_000;

/**
 * Returns a getAccessToken that waits for AuthKit to finish initializing.
 *
 * AuthKit's provider initializes in a useEffect, so on the first render the
 * context exposes a NOOP client whose getAccessToken rejects immediately with
 * LoginRequiredError. This causes every data hook (useMe, useSessions,
 * useAgents, useNotifications) to fail if it runs before auth is ready.
 *
 * By wrapping the call, we block callers until the real client is available,
 * then forward to the real getAccessToken. If the user ends up unauthenticated,
 * we throw LoginRequiredError so the hooks still fail correctly.
 */
function useAuthReadyGetAccessToken(
  authKit: ReturnType<typeof useAuthKit>,
  testMode: boolean
): AuthKitAccessTokenGetter {
  const isLoadingRef = useRef(authKit.isLoading);
  const userRef = useRef(authKit.user);
  const getTokenRef = useRef(authKit.getAccessToken);

  isLoadingRef.current = authKit.isLoading;
  userRef.current = authKit.user;
  getTokenRef.current = authKit.getAccessToken;

  return useCallback(async (options?: AuthKitAccessTokenOptions) => {
    if (testMode) {
      return fakeGetAccessToken(options);
    }

    const start = Date.now();
    while (isLoadingRef.current && Date.now() - start < MAX_AUTH_WAIT_MS) {
      await wait(100);
    }

    if (!userRef.current) {
      throw new LoginRequiredError();
    }

    return getTokenRef.current(options);
  }, [testMode]);
}

export function useAuthWithTestMode() {
  const authKit = useAuthKit();
  const testMode = useMemo(isTestMode, []);
  const signIn = useCallback((...args: Parameters<typeof authKit.signIn>) => {
    markWorkOSAuthorizationStarted();
    return authKit.signIn(...args).catch((error) => {
      clearWorkOSPendingAuthorization();
      throw error;
    });
  }, [authKit.signIn]);

  if (testMode) {
    return {
      user: TEST_USER,
      isLoading: false,
      signIn: () => {}, // no-op: prevent redirect to WorkOS
      signOut: () => {}, // no-op
      getAccessToken: fakeGetAccessToken,
      // Compatibility with AuthKit shape
      organization: null,
      organizations: [],
      role: null,
      permissions: [],
      impersonator: null,
    };
  }

  const getAccessToken = useAuthReadyGetAccessToken(authKit, testMode);

  return {
    ...authKit,
    signIn,
    getAccessToken,
  };
}
